<template>
  <div class="role-container">
    <el-card shadow="never">
      <!-- 工具栏 -->
      <div class="toolbar">
        <el-input
          v-model.trim="keyword"
          placeholder="搜索角色名"
          clearable
          prefix-icon="el-icon-search"
          style="width: 240px"
          @keyup.enter.native="handleSearch"
          @clear="handleSearch"
        />
        <el-button type="primary" icon="el-icon-search" @click="handleSearch">搜索</el-button>
        <el-button type="success" icon="el-icon-plus" @click="handleAdd">新增角色</el-button>
      </div>

      <!-- 表格 -->
      <el-table v-loading="loading" :data="tableData" border stripe style="width: 100%">
        <el-table-column type="index" label="#" width="50" align="center" />
        <el-table-column prop="roleName" label="角色名" min-width="140" />

        <!-- 业务系统权限 -->
        <el-table-column label="业务系统权限" min-width="260">
          <template slot-scope="{ row }">
            <span v-if="!row.permission || !row.permission.length" class="perm-empty">
              未配置
            </span>
            <el-tooltip v-else placement="top" :disabled="summarize(row.permission).length <= 3">
              <div slot="content" class="perm-tip-box">
                <div v-for="s in summarize(row.permission)" :key="s.path">{{ describeItem(s) }}</div>
              </div>
              <div class="perm-tags">
                <el-tag
                  v-for="s in summarize(row.permission).slice(0, 3)"
                  :key="s.path"
                  size="mini"
                  :type="permType(s.path)"
                >
                  {{ describeItem(s) }}
                </el-tag>
                <el-tag v-if="summarize(row.permission).length > 3" size="mini">
                  +{{ summarize(row.permission).length - 3 }}
                </el-tag>
              </div>
            </el-tooltip>
          </template>
        </el-table-column>

        <el-table-column label="覆盖页面数" width="110" align="center">
          <template slot-scope="{ row }">
            <span>{{ countPages(row.permission) }}</span>
          </template>
        </el-table-column>

        <el-table-column prop="remark" label="备注" min-width="140">
          <template slot-scope="{ row }">
            <span v-if="row.remark">{{ row.remark }}</span>
            <span v-else class="perm-empty">-</span>
          </template>
        </el-table-column>

        <el-table-column label="操作" width="180" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" icon="el-icon-edit" @click="handleEdit(row)">
              编辑
            </el-button>
            <el-button
              type="text"
              size="small"
              class="delete-btn"
              icon="el-icon-delete"
              @click="handleDelete(row)"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
          :current-page.sync="page"
          :page-size="size"
          :page-sizes="[10, 20, 50]"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          background
          @size-change="handleSizeChange"
          @current-change="fetchData"
        />
      </div>
    </el-card>

    <!-- 新增/编辑弹窗 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="680px"
      :close-on-click-modal="false"
    >
      <el-form ref="form" :model="form" :rules="rules" label-width="90px">
        <el-form-item label="角色名" prop="roleName">
          <el-input v-model.trim="form.roleName" placeholder="请输入角色名" />
        </el-form-item>

        <el-form-item label="业务系统权限">
          <div class="perm-summary">
            共 {{ treeTotal }} 个页面，已选 {{ selectedPerms.length }} 项权限，
            覆盖 {{ selectedLeafCount }} 个页面（模块以整体计）
          </div>

          <div class="perm-search">
            <el-input
              v-model.trim="permFilter"
              placeholder="搜索模块 / 页面"
              clearable
              size="small"
              prefix-icon="el-icon-search"
            />
            <el-button size="small" @click="checkAll">全选</el-button>
            <el-button size="small" @click="clearChecked">清空</el-button>
          </div>

          <div class="perm-tree-box">
            <el-tree
              ref="permTree"
              :data="permTree"
              :props="treeProps"
              node-key="path"
              show-checkbox
              check-strictly
              default-expand-all
              :filter-node-method="filterNode"
              @check-change="onCheckChange"
            >
              <span slot-scope="{ node, data }" class="perm-node">
                <el-tag :type="permType(data.path)" size="mini">
                  {{ data.level === 'module' ? '模块' : '页面' }}
                </el-tag>
                <span class="perm-node-label">{{ data.title }}</span>
                <span class="perm-node-path">{{ data.path }}</span>
                <span v-if="coveredCount(data.path)" class="perm-node-cover">
                  含其下 {{ coveredCount(data.path) }} 项
                </span>
              </span>
            </el-tree>
          </div>

          <div class="perm-tip">
            勾选<b>模块</b>即拥有该模块下全部页面；也可只勾选<b>单个页面</b>。
            勾选某节点时会自动移除其<b>上级</b>与<b>下级</b>（二者互相覆盖），无需手动清理。
          </div>

          <div v-if="selectedSummary.length" class="perm-selected">
            <div class="perm-selected-title">
              已选 {{ selectedSummary.length }} 项，覆盖 {{ selectedLeafCount }} 个页面：
            </div>
            <el-tag
              v-for="s in selectedSummary"
              :key="s.path"
              size="mini"
              :type="permType(s.path)"
              closable
              @close="removePerm(s.path)"
            >
              {{ describeItem(s) }}
            </el-tag>
          </div>
        </el-form-item>

        <el-form-item label="备注">
          <el-input v-model.trim="form.remark" placeholder="选填" />
        </el-form-item>
      </el-form>

      <div slot="footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" :loading="submitting" @click="handleSubmit">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getRoleList, createRole, updateRole, deleteRole } from '@/api/role';
import {
  toTree,
  buildPermission,
  parsePermission,
  summarize,
  countPages,
  describeItem
} from '@/utils/permission';

export default {
  name: 'Role',
  data() {
    return {
      loading: false,
      tableData: [],
      keyword: '',
      page: 1,
      size: 10,
      total: 0,
      // 业务系统权限树（模块 -> 页面），直接来自 utils/businessRoutes.js
      permTree: toTree(),
      permFilter: '',
      // 树当前勾选的 key（完整路径），与计算属性解耦避免互相触发
      rawCheckedKeys: [],
      // 弹窗
      dialogVisible: false,
      submitting: false,
      isEdit: false,
      form: {
        id: '',
        roleName: '',
        permission: [],
        remark: ''
      },
      rules: {
        roleName: [{ required: true, message: '请输入角色名', trigger: 'blur' }]
      }
    };
  },
  computed: {
    dialogTitle() {
      return this.isEdit ? '编辑角色' : '新增角色';
    },
    treeProps() {
      return { children: 'children', label: 'title' };
    },
    // 树中出现过的全部有效路径（回显时过滤失效项）
    validPaths() {
      const set = new Set();
      const walk = nodes => {
        (nodes || []).forEach(n => {
          set.add(n.path);
          if (n.children && n.children.length) walk(n.children);
        });
      };
      walk(this.permTree);
      return set;
    },
    // 全部叶子页面数
    treeTotal() {
      let n = 0;
      const walk = nodes => {
        (nodes || []).forEach(c => {
          if (c.children && c.children.length) walk(c.children);
          else n++;
        });
      };
      walk(this.permTree);
      return n;
    },
    // 当前勾选 -> 保存用的 permission（完整树：模块 + 扁平副本）
    selectedPerms() {
      return buildPermission(this.rawCheckedKeys);
    },
    // 展示用摘要（不含扁平副本）
    selectedSummary() {
      return summarize(this.selectedPerms);
    },
    // 当前勾选项实际覆盖的页面数
    selectedLeafCount() {
      return countPages(this.selectedPerms);
    }
  },
  watch: {
    permFilter(val) {
      this.$refs.permTree && this.$refs.permTree.filter(val);
    }
  },
  created() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const { data } = await getRoleList({
          page: this.page,
          size: this.size,
          keyword: this.keyword
        });
        this.tableData = data.data.list;
        this.total = data.data.total;
      } catch (err) {
        this.$message.error(err.message);
      } finally {
        this.loading = false;
      }
    },
    handleSearch() {
      this.page = 1;
      this.fetchData();
    },
    handleSizeChange(size) {
      this.size = size;
      this.page = 1;
      this.fetchData();
    },

    /* ---------- 权限相关 ---------- */
    // 取祖先路径：'/a/b/c' => ['/a', '/a/b']
    ancestorsOf(path) {
      const parts = String(path).replace(/^\//, '').split('/');
      const out = [];
      for (let i = 0; i < parts.length - 1; i++) {
        out.push('/' + parts.slice(0, i + 1).join('/'));
      }
      return out;
    },
    // 勾选变化：勾中节点时自动移除其祖先与后代
    onCheckChange(nodeData, checked) {
      if (this._syncing) return;
      const tree = this.$refs.permTree;
      if (!tree) return;

      if (!checked) {
        this.rawCheckedKeys = tree.getCheckedKeys() || [];
        return;
      }

      const path = nodeData.path;
      const ancestors = this.ancestorsOf(path);
      const keys = (tree.getCheckedKeys() || []).filter(k => {
        if (k === path) return true;
        if (ancestors.indexOf(k) !== -1) return false;     // 去掉祖先
        if (k.indexOf(path + '/') === 0) return false;     // 去掉后代
        return true;
      });

      this.rawCheckedKeys = keys;
      this.applyChecked(keys);
    },
    // 统一写回树，加 guard 防止 setCheckedKeys 触发 check-change 递归
    applyChecked(keys) {
      this._syncing = true;
      this.$nextTick(() => {
        if (this.$refs.permTree) this.$refs.permTree.setCheckedKeys(keys, false);
        this.$nextTick(() => { this._syncing = false; });
      });
    },
    // 回显：树形 permission -> 勾选路径（会自动剔除业务系统中已不存在的路径）
    setCheckedPerms(permission) {
      const keys = parsePermission(permission);
      this.rawCheckedKeys = keys;
      this.applyChecked(keys);
    },
    checkAll() {
      // 全选 = 勾所有顶层模块 + 独立页面，等价于全权限且更简洁
      const roots = this.permTree.map(n => n.path);
      this.rawCheckedKeys = roots;
      this.applyChecked(roots);
    },
    clearChecked() {
      this.rawCheckedKeys = [];
      this.applyChecked([]);
    },
    removePerm(p) {
      this.rawCheckedKeys = this.rawCheckedKeys.filter(k => k !== p);
      this.applyChecked(this.rawCheckedKeys);
    },
    filterNode(value, data) {
      if (!value) return true;
      const v = String(value).toLowerCase();
      return String(data.path).toLowerCase().indexOf(v) !== -1
        || String(data.title).toLowerCase().indexOf(v) !== -1;
    },
    // 节点下含多少后代
    coveredCount(path) {
      const node = this.findNode(this.permTree, path);
      if (!node) return 0;
      let n = 0;
      const walk = list => { (list || []).forEach(c => { n++; walk(c.children); }); };
      walk(node.children);
      return n;
    },
    findNode(nodes, path) {
      for (const n of nodes || []) {
        if (n.path === path) return n;
        const hit = this.findNode(n.children, path);
        if (hit) return hit;
      }
      return null;
    },
    // 权限项 -> 展示文案（模块「角色模块（全部 20 项）」，页面直接标题）
    describeItem(item) {
      return describeItem(item);
    },
    // 表格展示：把完整树压缩成「模块 + 独立页面」，不显示扁平副本
    summarize(permission) {
      return permission && permission.length ? summarize(permission) : [];
    },
    // 模块 / 页面 配色
    permType(p) {
      const hit = this.findNode(this.permTree, p);
      if (hit && hit.level === 'module') return 'warning';
      return 'info';
    },
    // 表格里展示该角色实际覆盖多少页面
    countPages(permission) {
      return permission && permission.length ? countPages(permission) : 0;
    },

    /* ---------- 弹窗 ---------- */
    handleAdd() {
      this.isEdit = false;
      this.form = { id: '', roleName: '', permission: [], remark: '' };
      this.permFilter = '';
      this.dialogVisible = true;
      this.$nextTick(() => {
        this.$refs.form.clearValidate();
        this.setCheckedPerms([]);
      });
    },
    handleEdit(row) {
      this.isEdit = true;
      this.form = {
        id: row._id,
        roleName: row.roleName,
        permission: row.permission || [],
        remark: row.remark || ''
      };
      this.permFilter = '';
      this.dialogVisible = true;
      this.$nextTick(() => {
        this.$refs.form.clearValidate();
        this.setCheckedPerms(row.permission);
      });
    },
    async handleSubmit() {
      this.$refs.form.validate(async valid => {
        if (!valid) return;
        this.submitting = true;
        try {
          // 以树的真实勾选状态为准，避免依赖中间变量导致提交为空
          const tree = this.$refs.permTree;
          const keys = tree ? (tree.getCheckedKeys() || []) : this.rawCheckedKeys;
          const payload = Object.assign({}, this.form, {
            permission: buildPermission(keys)
          });
          if (this.isEdit) {
            await updateRole(payload);
          } else {
            await createRole(payload);
          }
          this.$message.success(this.isEdit ? '更新成功' : '创建成功');
          this.dialogVisible = false;
          this.fetchData();
        } catch (err) {
          this.$message.error(err.message);
        } finally {
          this.submitting = false;
        }
      });
    },
    handleDelete(row) {
      this.$confirm(`确定删除角色 "${row.roleName}" 吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await deleteRole(row._id);
          this.$message.success('删除成功');
          if (this.tableData.length === 1 && this.page > 1) {
            this.page--;
          }
          this.fetchData();
        } catch (err) {
          this.$message.error(err.message);
        }
      }).catch(() => {});
    }
  }
};
</script>

<style scoped>
.role-container {
  padding: 10px;
}
.toolbar {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}
.delete-btn {
  color: #f56c6c;
}
.pagination {
  margin-top: 20px;
  text-align: right;
}

/* 权限列 */
.perm-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.perm-empty {
  color: #c0c4cc;
}
.perm-tip-box {
  max-height: 300px;
  overflow: auto;
  line-height: 1.9;
}

/* 权限树 */
.perm-summary {
  margin-bottom: 8px;
  font-size: 12px;
  color: #606266;
}
.perm-search {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}
.perm-tree-box {
  width: 100%;
  max-height: 340px;
  padding: 8px;
  overflow: auto;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
}
.perm-node {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
}
.perm-node-label {
  color: #606266;
}
.perm-node-path {
  font-size: 12px;
  color: #c0c4cc;
}
.perm-node-cover {
  margin-left: 2px;
  font-size: 12px;
  color: #c0c4cc;
}

/* 弹窗内提示 */
.perm-tip {
  margin-top: 6px;
  font-size: 12px;
  line-height: 1.6;
  color: #909399;
}
.perm-tip b {
  color: #606266;
}
.perm-selected {
  margin-top: 8px;
  padding: 8px;
  background: #f5f7fa;
  border-radius: 4px;
}
.perm-selected-title {
  margin-bottom: 6px;
  font-size: 12px;
  color: #606266;
}
.perm-selected .el-tag {
  margin: 0 4px 4px 0;
}
</style>
